import { normaliserSociete } from './relances.js';
/**
 * Demande d'enlèvement DHL Freight pour les commandes stockées chez HIOLLE.
 *
 * HIOLLE Logistique entrepose les gammes AER, TPZ et MAX. L'enlèvement s'y
 * commande chez DHL Fret : DHL vient prendre les palettes chez HIOLLE et les
 * livre au client. Mosaik est l'expéditeur (`Consignor`), HIOLLE le lieu
 * d'enlèvement (`Pickup`), le client le destinataire (`Consignee`).
 *
 * Tout ce fichier est PUR : il ne parle à personne, il construit un corps de
 * requête et dit ce qui manque. C'est ce qui permet de le vérifier sans clé
 * d'API et sans réseau — une réservation de transport est une écriture chez un
 * tiers, elle ne se teste pas en la déclenchant.
 *
 * Reprend le scénario n8n « Automatisation demande d'enlèvement », dont les
 * écarts assumés sont notés au fil du fichier.
 */
/**
 * La commande passe par HIOLLE : l'enlèvement est à NOTRE charge.
 *
 * Partout ailleurs le fournisseur expédie lui-même et annonce sa date. Chez
 * HIOLLE la marchandise attend qu'on fasse venir DHL : attendre de leur part
 * une date d'expédition, c'est attendre ce qui ne viendra pas.
 */
export function chezHiolle(fournisseurs) {
    return fournisseurs.some((f) => normaliserSociete(f) === 'HIOLLE');
}
/**
 * À qui l'on livre : le nom du destinataire saisi dans la demande d'enlèvement,
 * sinon la société, sinon le client — ce qui partait chez les transporteurs
 * avant ce champ.
 */
export function nomLivraison(d) {
    return (d.adresse?.nom ?? '').trim() || (d.entreprise ?? '').trim() || (d.client ?? '').trim();
}
/** Une anomalie qui empêche de construire l'instruction. */
export class EnlevementInvalide extends Error {
    raisons;
    constructor(raisons) {
        super(raisons.join(' · '));
        this.raisons = raisons;
        this.name = 'EnlevementInvalide';
    }
}
/** Nombre maximal de palettes acceptées dans une instruction. */
export const PALETTES_MAX = 7;
/**
 * Codes pays ISO, depuis un nom en clair.
 *
 * La base porte « France », « Belgique », parfois déjà « FR ». Un pays inconnu
 * n'est PAS ramené à `FR` en silence — c'était le comportement du scénario n8n,
 * et il expédiait en France une palette destinée ailleurs. On préfère refuser.
 */
const PAYS = {
    france: 'FR',
    belgique: 'BE', belgium: 'BE',
    italie: 'IT', italy: 'IT', italia: 'IT',
    espagne: 'ES', spain: 'ES', espana: 'ES',
    allemagne: 'DE', germany: 'DE', deutschland: 'DE',
    suisse: 'CH', switzerland: 'CH',
    luxembourg: 'LU',
    'pays-bas': 'NL', netherlands: 'NL', holland: 'NL', nederland: 'NL',
    'royaume-uni': 'GB', 'united kingdom': 'GB', uk: 'GB',
    portugal: 'PT',
    autriche: 'AT', austria: 'AT',
    pologne: 'PL', poland: 'PL',
    suede: 'SE', sweden: 'SE', 'suède': 'SE',
    norvege: 'NO', norway: 'NO', 'norvège': 'NO',
    danemark: 'DK', denmark: 'DK',
    finlande: 'FI', finland: 'FI',
    irlande: 'IE', ireland: 'IE',
    tchequie: 'CZ', 'tchéquie': 'CZ', 'czech republic': 'CZ', 'republique tcheque': 'CZ',
    grece: 'GR', greece: 'GR', 'grèce': 'GR',
    roumanie: 'RO', romania: 'RO',
    hongrie: 'HU', hungary: 'HU',
};
/**
 * Code ISO d'un pays, ou `null` s'il n'est pas reconnu.
 *
 * `null` plutôt que `'FR'` : deviner la France sur un nom inconnu enverrait la
 * marchandise au mauvais endroit sans que rien ne le signale.
 */
export function codePays(pays) {
    const brut = String(pays ?? '').trim();
    if (!brut)
        return null;
    if (/^[A-Za-z]{2}$/.test(brut))
        return brut.toUpperCase();
    const cle = brut.toLowerCase().replace(/\s+/g, ' ');
    return PAYS[cle] ?? null;
}
/** Samedi ou dimanche. */
function estWeekend(d) {
    const j = d.getUTCDay();
    return j === 0 || j === 6;
}
/**
 * Prochain jour ouvré, en UTC.
 *
 * `new Date()` local puis `toISOString()` — ce que faisait le scénario n8n —
 * décale d'un jour pour toute exécution du soir en heure d'été française. Ici
 * tout est calculé en UTC à partir d'un jour ISO, donc sans fuseau du tout.
 *
 * Les jours fériés ne sont PAS connus : DHL refuse ou décale l'enlèvement le cas
 * échéant, et c'est lui qui fait foi. Le noter ici plutôt que de maintenir un
 * calendrier qui se périmerait en silence.
 */
export function prochainJourOuvre(depuis, sauter = 1) {
    const d = new Date(`${depuis}T00:00:00Z`);
    let reste = sauter;
    while (reste > 0) {
        d.setUTCDate(d.getUTCDate() + 1);
        if (!estWeekend(d))
            reste -= 1;
    }
    return d.toISOString().slice(0, 10);
}
/**
 * Palettes — ou colis — annoncées dans un texte libre.
 *
 * HIOLLE répond au mail de commande en décrivant son colisage. Le scénario n8n
 * confie cette lecture à un modèle de langage ; ici elle est déterministe, et
 * le résultat est de toute façon RELU à l'écran avant la réservation — ce qui
 * rend l'exactitude du parseur moins critique que sa prévisibilité.
 *
 * Formes reconnues, insensibles à la casse et aux accents :
 *
 *   « 120 x 80 x 95 cm - 450 kg »                       une unité par ligne
 *   « L120 l80 H95 450kg », « longueur 120 largeur 80 hauteur 95 poids 450 »
 *
 * et celles qu'emploie réellement HIOLLE, sur plusieurs lignes :
 *
 *   « 1 palette 115x117 / Hauteur : 70cm / Poids : 250kgs »
 *   « 2 colis 100x100x20 / Poids par colis : 18kgs »
 *   « 2 colis 100x100x20 / Poids colis 1 : 18kgs / Poids colis 2 : 3kgs »
 *   « 2 palettes 115x117 / Palette 1 : Hauteur : 190cm poids : 170kgs
 *     / Palette 2 : Hauteur : 170cm poids : 150kgs »
 *   « 2 palettes 115x117 / P1 : Hauteur : 110cm poids 540kgs
 *     / P2 : Hauteur : 80cm poids 360kgs »
 *
 * Une ligne qui porte des dimensions ouvre un groupe ; les lignes suivantes le
 * complètent (hauteur, poids) jusqu'aux dimensions suivantes. Le nombre
 * d'unités n'est retenu que s'il est sur la MÊME ligne que les dimensions :
 * « 2 palettes : » seul n'est qu'un en-tête, suivi d'une ligne par palette.
 * Une ligne qui commence par « Palette 2 : » ou « Colis 2 : » ne décrit que
 * cette unité : sa hauteur et son poids ne valent pas pour les autres.
 *
 * Le poids se lit de quatre façons : par unité (« par colis »), unité par unité
 * (« colis 1 : »), comme le poids de l'envoi s'il est dit total (« poids
 * total », « en tout »), réparti également — et sinon comme le poids de
 * CHAQUE unité. Ce dernier cas est un pari dès qu'il y a plusieurs unités :
 * `lirePalettes` le signale, et l'écran le montre.
 *
 * Seule la réponse est lue : l'historique cité sous « De : … » est écarté, pour
 * qu'une ancienne annonce ne s'ajoute pas à la nouvelle.
 *
 * Les dimensions sont en centimètres et le poids en kilogrammes. Un groupe
 * incomplet est abandonné : mieux vaut une palette manquante, que l'écran
 * montre, qu'une palette inventée.
 */
export function extrairePalettes(texte) {
    return lirePalettes(texte).palettes;
}
/**
 * `extrairePalettes`, avec ce que la lecture a dû supposer.
 *
 * « 2 palettes 115x117 / Poids : 110kgs » ne dit pas si 110 kg est le poids de
 * chaque palette ou de l'envoi. On lit le poids de chaque palette — c'est
 * ainsi qu'écrit HIOLLE —, mais on le dit, pour que l'écran le fasse vérifier.
 */
export function lirePalettes(texte) {
    const lignes = lignesDeReponse(texte);
    const palettes = [];
    const avertissements = [];
    let g = null;
    const clore = () => {
        if (!g)
            return;
        const nombreUnites = Math.max(g.nombre, g.individuels.length, g.hauteurs.length);
        const unites = Array.from({ length: nombreUnites }, (_, i) => ({
            longueur: g.longueur,
            largeur: g.largeur,
            hauteur: g.hauteurs[i] ?? g.hauteur,
            poids: g.individuels[i] ?? g.parUnite
                ?? (g.total === null ? null : Number((g.total / nombreUnites).toFixed(1)))
                ?? g.generique,
        }));
        // Tout le groupe ou rien : une unité sans poids rendrait les autres douteuses.
        if (unites.every(valide)) {
            for (const u of unites)
                if (palettes.length < PALETTES_MAX)
                    palettes.push(u);
            const generique = g.generique;
            const suppose = nombreUnites > 1 && generique !== null && g.total === null && g.parUnite === null
                && unites.some((_, i) => g.individuels[i] === undefined);
            if (suppose) {
                const chaque = g.colis ? 'colis' : 'palette';
                avertissements.push(`${generique} kg lu par ${chaque} (${Number((generique * nombreUnites).toFixed(1))} kg en tout)`
                    + ' : le mail ne dit pas si c’est le total.');
            }
        }
        g = null;
    };
    for (const l of lignes) {
        const trois = l.match(TROIS_DIMENSIONS);
        const deux = trois ? null : l.match(DEUX_DIMENSIONS);
        const n = nommees(l);
        const ouvre = Boolean(trois || deux || (n.longueur !== null && n.largeur !== null));
        if (ouvre) {
            clore();
            const annonce = l.match(NOMBRE_UNITES);
            g = {
                nombre: annonce ? Math.max(1, Number(annonce[1])) : 1,
                colis: annonce ? /colis/i.test(annonce[0]) : false,
                longueur: null, largeur: null, hauteur: null,
                total: null, generique: null, parUnite: null, individuels: [], hauteurs: [],
            };
        }
        if (!g)
            continue;
        if (trois) {
            g.longueur = nombre(trois, 1);
            g.largeur = nombre(trois, 2);
            g.hauteur = nombre(trois, 3);
        }
        else if (deux) {
            g.longueur = nombre(deux, 1);
            g.largeur = nombre(deux, 2);
        }
        g.longueur ??= n.longueur;
        g.largeur ??= n.largeur;
        // « Palette 2 : Hauteur : 170cm poids : 150kgs » ne vaut que pour la 2e.
        const unite = ouvre ? null : l.match(RANG_UNITE);
        const rangUnite = unite ? Number(unite[1]) : 0;
        const pourUnite = rangUnite >= 1 && rangUnite <= PALETTES_MAX;
        if (n.hauteur !== null) {
            if (pourUnite)
                g.hauteurs[rangUnite - 1] = n.hauteur;
            else
                g.hauteur = n.hauteur;
        }
        const individuel = l.match(POIDS_INDIVIDUEL);
        const poids = poidsDe(l);
        if (individuel) {
            const rang = Number(individuel[1]);
            if (rang >= 1 && rang <= PALETTES_MAX)
                g.individuels[rang - 1] = nombre(individuel, 2) ?? undefined;
        }
        else if (poids !== null && pourUnite) {
            g.individuels[rangUnite - 1] = poids;
        }
        else if (poids !== null && PAR_UNITE.test(l)) {
            g.parUnite = poids;
        }
        else if (poids !== null && POIDS_TOTAL.test(l)) {
            g.total = poids;
        }
        else if (poids !== null) {
            g.generique = poids;
        }
    }
    clore();
    return { palettes, avertissements };
}
/**
 * Les lignes de la RÉPONSE, texte brut, sans accents ni balises.
 *
 * Seule la réponse est lue : l'historique cité sous « De : … » est écarté.
 */
function lignesDeReponse(texte) {
    return reponseSeule(String(texte ?? '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/<br\s*\/?>/gi, '\n')
        // Les balises de BLOC deviennent des retours à la ligne, les autres
        // disparaissent. Sans cela, deux palettes décrites dans deux `<div>` se
        // retrouvent sur une seule ligne et sont lues comme une seule — le corps
        // d'un mail Outlook arrive en HTML, pas en texte.
        .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .split(/[\n;]+/)
        .map((l) => l.trim())
        .filter(Boolean));
}
/**
 * HIOLLE annonce des COLIS, et non des palettes : l'envoi part par DPD.
 *
 * DHL Freight ne vient chercher que des palettes. Des colis (« 2 colis
 * 100x100x20 », `#MF3411`) se confient à DPD, par `construireDemandeDpd`.
 * Seule la réponse compte : l'historique cité est écarté.
 */
export function annonceDeColis(texte) {
    return transportAnnonce(texte) === 'dpd';
}
/**
 * Le transporteur que le mail de HIOLLE IMPOSE, ou `null` s'il ne dit rien.
 *
 * Une palette part chez DHL, même accompagnée de colis (« 1 palette, soit 12
 * colis ») ; des colis seuls partent chez DPD. Quand le mail parle de l'un ou
 * de l'autre, c'est lui qui décide : l'écran ne peut plus choisir, sans quoi
 * un clic enverrait des palettes à DPD, qui ne les prend pas. Sans mail, ou
 * d'un mail muet sur le colisage, le choix revient à l'écran.
 */
export function transportAnnonce(texte) {
    const reponse = lignesDeReponse(texte).join('\n');
    if (/\bpalettes?\b/i.test(reponse))
        return 'dhl';
    if (/\bcolis\b/i.test(reponse))
        return 'dpd';
    return null;
}
const NOMBRE = String.raw `(\d+(?:[.,]\d+)?)`;
const TROIS_DIMENSIONS = new RegExp(`${NOMBRE}\\s*[x*]\\s*${NOMBRE}\\s*[x*]\\s*${NOMBRE}`, 'i');
const DEUX_DIMENSIONS = new RegExp(`${NOMBRE}\\s*[x*]\\s*${NOMBRE}`, 'i');
/** « 450 kg », « 250kgs », « 300 kilos ». */
const POIDS_UNITE = new RegExp(`${NOMBRE}\\s*(?:kgs?|kilos?)\\b`, 'i');
const POIDS_NOMME = new RegExp(`poids\\s*(?:(?:brut|net)\\s*)?(?:total\\s*)?:?\\s*${NOMBRE}`, 'i');
/** « Poids total : 500 », « 500 kg en tout », « au total ». */
const POIDS_TOTAL = /\btota(?:l|le|ux)\b|\ben\s+tout\b/i;
/** « 2 palettes », « 1 colis ». */
const NOMBRE_UNITES = /(\d+)\s*(?:palettes?|colis)\b/i;
/** « Poids colis 1 : 18kgs », « poids palette n°2 : 300 ». */
const POIDS_INDIVIDUEL = new RegExp(`poids\\s*(?:du\\s+|de\\s+la\\s+)?(?:colis|palette)\\s*(?:n\\s*[°o.]?\\s*)?(\\d+)\\s*:?\\s*${NOMBRE}`, 'i');
/** « Palette 1 : … », « P1 : … », « Pal. 2 - … », « Colis n°2 : … », en tête de ligne. */
const RANG_UNITE = /^(?:palette|pal\.?|p|colis)\s*(?:n\s*[°o.]?\s*)?(\d+)\s*[:\-–]/i;
/** « par colis », « par palette », « chacun ». */
const PAR_UNITE = /\bpar\s+(?:colis|palette)|\bchacune?\b|\/\s*(?:colis|palette)/i;
function nombre(m, i = 1) {
    return m?.[i] === undefined ? null : Number(String(m[i]).replace(',', '.'));
}
function poidsDe(l) {
    return nombre(l.match(POIDS_UNITE) ?? l.match(POIDS_NOMME));
}
function nommees(l) {
    return {
        longueur: nombre(l.match(new RegExp(`long(?:ueur)?\\s*:?\\s*${NOMBRE}`, 'i'))
            ?? l.match(new RegExp(`\\bL\\s*:?\\s*${NOMBRE}`))),
        largeur: nombre(l.match(new RegExp(`larg(?:eur)?\\s*:?\\s*${NOMBRE}`, 'i'))
            ?? l.match(new RegExp(`\\bl\\s*:?\\s*${NOMBRE}`))),
        hauteur: nombre(l.match(new RegExp(`haut(?:eur)?\\s*:?\\s*${NOMBRE}`, 'i'))
            ?? l.match(new RegExp(`\\bH\\s*:?\\s*${NOMBRE}`))),
    };
}
function valide(p) {
    return [p.longueur, p.largeur, p.hauteur, p.poids]
        .every((n) => n !== null && Number.isFinite(n) && n > 0);
}
/**
 * Coupe l'historique cité : tout ce qui suit « De : », « From: », « -----Original »
 * ou « … a écrit : ». C'est NOTRE mail de commande, ou un échange plus ancien.
 */
function reponseSeule(lignes) {
    const fin = lignes.findIndex((l) => /^(de|from|envoye|sent)\s*:/i.test(l)
        || /^-{2,}\s*(original|message)/i.test(l)
        || /a ecrit\s*:?$/i.test(l));
    return fin < 0 ? lignes : lignes.slice(0, fin);
}
/**
 * Adresse lue dans un bloc de texte libre, séparé par des `<br />`.
 *
 * Certaines commandes n'ont pas d'adresse dans les champs dédiés : tout est
 * dans le champ « détails de livraison », sous la forme que Sellsy exporte :
 *
 *   Adresse de livraison<br />Maxime Vrel<br />113 Avenue Gilbert Roux
 *   <br />Cusset 03300<br />France<br />+33 6 60 50 88 23
 *
 * Le scénario n8n lisait ce bloc par INDICES fixes — `segments[2]` la rue,
 * `segments[3]` la ville — et exigeait au moins cinq segments. Une ligne en
 * plus ou en moins, un complément d'adresse, un bloc sans en-tête, et tout
 * décalait en silence : la ville devenait la rue.
 *
 * Ici on cherche des REPÈRES plutôt que des positions. Le code postal est le
 * point d'ancrage — c'est le seul élément reconnaissable à coup sûr : ce qui
 * l'entoure est la ville, ce qui précède est la rue, ce qui suit est le pays.
 */
export function analyserBlocAdresse(texte) {
    const lignes = String(texte ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/&nbsp;/gi, ' ')
        .split(/\n+/)
        .map((l) => l.replace(/<[^>]+>/g, '').trim())
        .filter(Boolean);
    if (!lignes.length)
        return null;
    /*
     * L'en-tête « Adresse de livraison » n'est pas une donnée, et ce qui le
     * PRÉCÈDE non plus : Sellsy y place parfois d'autres rubriques. Sur
     * `FAC-20260921-39501`, « Date de livraison : mi-octobre 2026 » ouvrait le
     * bloc, et son « 2026 » passait pour un code postal — rien ne le précédant,
     * l'adresse entière était déclarée illisible.
     */
    const ENTETE = /^adresse\s+de\s+livraison\s*:?$/i;
    const iEntete = lignes.findIndex((l) => ENTETE.test(l));
    const utiles = (iEntete >= 0 ? lignes.slice(iEntete + 1) : lignes).filter((l) => !ENTETE.test(l));
    const estTelephone = (l) => /^[+()\d][\d\s.\-()]{7,}$/.test(l) && (l.match(/\d/g) ?? []).length >= 9;
    const telephone = utiles.find(estTelephone) ?? null;
    const sansTel = utiles.filter((l) => l !== telephone);
    /*
     * Le code postal ancre tout le reste : 4 ou 5 chiffres isolés sur la ligne,
     * éventuellement précédés du préfixe pays — « L-4570 Niederkorn » au
     * Luxembourg, « B-1000 » en Belgique. Sans ce préfixe, le tiret collé
     * empêchait toute lecture (`FAC-20261007-39534`) : l'adresse entière
     * passait pour absente. Le préfixe ne fait pas partie du code.
     */
    const CODE_POSTAL = /(^|\s)(?:[A-Z]{1,3}-)?(\d{4,5})(?=\s|$)/;
    // La rue précède toujours le code postal : une ligne en tête n'en est pas un.
    const iVille = sansTel.findIndex((l, i) => i > 0 && CODE_POSTAL.test(l));
    if (iVille < 0)
        return null;
    const ligneVille = sansTel[iVille];
    const trouve = ligneVille.match(CODE_POSTAL);
    const codePostal = trouve[2];
    const ville = ligneVille.replace(trouve[0], ' ').replace(/\s+/g, ' ').trim();
    // La rue est ce qui précède immédiatement. S'il y a plusieurs lignes avant,
    // la première est le nom du destinataire — l'export le place en tête.
    const avant = sansTel.slice(0, iVille);
    if (!avant.length)
        return null;
    const ligne = avant[avant.length - 1];
    const contact = avant.length > 1 ? avant[0] : null;
    const apres = sansTel.slice(iVille + 1);
    const pays = apres.length ? apres[0] : null;
    if (!ligne || !ville || !codePostal)
        return null;
    return { ligne, ville, codePostal, pays, contact, telephone };
}
/** Volume en m³ d'une palette, arrondi au litre. */
export function volumePalette(p) {
    return Number(((p.longueur / 100) * (p.largeur / 100) * (p.hauteur / 100)).toFixed(3));
}
/**
 * Mètres de plancher d'une palette.
 *
 * Convention DHL Fret : surface au sol rapportée à la largeur utile d'une
 * semi-remorque (240 cm), soit `L × l / 24000` en centimètres.
 */
export function metresPlancher(p) {
    return Number(((p.longueur * p.largeur) / 24000).toFixed(3));
}
/**
 * L'expéditeur, figé : c'est toujours Mosaik, DOMICILIÉ CHEZ HIOLLE.
 *
 * L'adresse est celle de l'entrepôt, pas celle du siège : c'est elle que DHL
 * imprime en tête de l'étiquette, et l'étiquette faite à la main porte
 * « MOSAIK / Rue Ambroise Croizat / HIOLLE LOGISTIQUE / 59125 Trith St Leger ».
 * Le siège de Saint-Maurice-de-Beynost n'y a pas sa place. Le numéro de
 * compte DHL et la TVA, eux, restent ceux de Mosaik.
 *
 * `id` est le compte de facturation DHL Freight de Mosaik — en `DAP`, c'est
 * l'expéditeur qui paie. Donné par Clément le 19/10/2026 ; il remplace
 * `1006865`, repris tel quel du scénario n8n d'origine.
 */
export const EXPEDITEUR_MOSAIK = {
    id: '20189420.FR0001',
    type: 'Consignor',
    vat: { countryCode: 'FR', number: 'FR95835038043' },
    name: 'MOSAIK',
    contactName: '',
    address: {
        street: 'Rue Ambroise Croizat',
        additionalAddressInfo: 'HIOLLE LOGISTIQUE',
        cityName: 'Trith St Leger',
        postalCode: '59125',
        countryCode: 'FR',
    },
    phone: '+33481918374',
    email: 'contact@mosaik-floor.fr',
};
/** Le lieu d'enlèvement, figé : c'est toujours l'entrepôt HIOLLE. */
export const ENLEVEMENT_HIOLLE = {
    type: 'Pickup',
    name: 'Hiolle',
    contactName: 'Antoine Lagache',
    address: {
        street: 'Rue Ambroise Croizat',
        additionalAddressInfo: '',
        cityName: 'TRITH ST LEGER',
        postalCode: '59125',
        countryCode: 'FR',
    },
    phone: '+33784279555',
    email: 'hiollelogistique@hiolle-industries.fr',
};
/**
 * Codes produit DHL Freight, imposés par DHL avant le passage en production
 * (relecture de nos envois de test, octobre 2026).
 *
 * SPD en France, ECI à l'international. ELD et ELI ne servent que lorsque
 * l'envoi ne peut pas partir en SPD ou ECI. **ECD ne doit jamais être
 * transmis** : c'était notre code domestique, DHL le refuse.
 */
export const PRODUITS_DHL = {
    standard: { domestique: 'SPD', international: 'ECI' },
    direct: { domestique: 'ELD', international: 'ELI' },
};
/**
 * Limites du DHL Road Freight Standard (SPD/ECI), le réseau de groupage.
 *
 * Relevées le 9 octobre 2026 sur l'API Produits (bac à sable, compte Mosaik)
 * et le manuel « Developer-First » 2026 R04, pour ECD/ECI : celles de SPD, qui
 * remplace ECD, restent à confirmer. Au-delà, l'envoi n'entre plus dans le
 * groupage et part en Direct (ELD/ELI) — un lot partiel, plus cher.
 *
 * Le poids par palette suit le manuel (1 500 kg) et non l'API Produits, qui
 * annonce 240 kg : `#MF3405`, une palette de 250 kg, est partie en Standard.
 */
export const LIMITES_STANDARD = {
    poidsTotal: 2500,
    volumeTotal: 10,
    metresPlancher: 1.51,
    poidsPalette: 1500,
    /** Centimètres : longueur, largeur, hauteur. */
    longueur: 400,
    largeur: 240,
    hauteur: 220,
};
/**
 * Type de palette DHL, d'après ses dimensions au sol.
 *
 * `PAL` (« Pallet », générique) partait pour toutes : la lettre de voiture du
 * `#MF3405`, faite à la main, portait `PLL` (« Pallet - large ») pour une
 * palette de 115 × 117. Dans la taille Europe (120 × 80, dans un sens ou dans
 * l'autre), c'est `PLZ` (« Pallet EURO size ») et non `PLE` (« Euro pallet ») :
 * le Z désigne chez DHL une palette sans échange (`HPLZ` « no pooling »), et
 * HIOLLE ne fait pas d'échange de palettes.
 */
export function typePaletteDhl(p) {
    const grand = Math.max(p.longueur, p.largeur);
    const petit = Math.min(p.longueur, p.largeur);
    return grand <= 120 && petit <= 80 ? 'PLZ' : 'PLL';
}
/** Pays desservis par DHL Freight mais hors de l'union douanière de l'UE. */
const HORS_UNION_DOUANIERE = new Set([
    'CH', 'LI', 'NO', 'GB', 'IS', 'AD', 'GI', 'RS', 'MK', 'AL', 'BA', 'ME', 'MD', 'XK', 'UA', 'TR', 'MA',
]);
/** Description douanière des marchandises. Une seule nature chez Mosaik. */
export const DESIGNATION_MARCHANDISE = 'Dalles de sol';
/**
 * Construit l'instruction de transport DHL, ou refuse en disant pourquoi.
 *
 * Refuse plutôt que de compléter : une adresse devinée, un pays ramené à la
 * France par défaut ou une palette au poids nul produiraient une réservation
 * fausse chez un tiers — et une réservation fausse se rattrape par téléphone,
 * pas par un correctif.
 */
export function construireInstructionDhl(d) {
    const raisons = [];
    const avertissements = [];
    const nom = nomLivraison(d);
    if (!nom)
        raisons.push('nom du destinataire');
    const rue = (d.adresse?.ligne ?? '').trim();
    const ville = (d.adresse?.ville ?? '').trim();
    const cp = (d.adresse?.codePostal ?? '').trim();
    if (!rue)
        raisons.push('adresse de livraison');
    if (!ville)
        raisons.push('ville');
    if (!cp)
        raisons.push('code postal');
    const pays = codePays(d.adresse?.pays);
    if (!pays) {
        raisons.push(d.adresse?.pays
            ? `pays non reconnu (« ${d.adresse.pays} »)`
            : 'pays');
    }
    const palettes = d.palettes ?? [];
    if (!palettes.length)
        raisons.push('au moins une palette');
    if (palettes.length > PALETTES_MAX) {
        raisons.push(`${palettes.length} palettes, maximum ${PALETTES_MAX}`);
    }
    palettes.forEach((p, i) => {
        const champs = [
            ['longueur', p.longueur], ['largeur', p.largeur], ['hauteur', p.hauteur], ['poids', p.poids],
        ];
        for (const [nomChamp, valeur] of champs) {
            if (!Number.isFinite(valeur) || valeur <= 0) {
                raisons.push(`palette ${i + 1} : ${nomChamp} manquant ou nul`);
            }
        }
    });
    if (raisons.length)
        throw new EnlevementInvalide(raisons);
    // Le téléphone et le courriel ne bloquent pas : DHL réserve sans eux, mais
    // la prise de rendez-vous de livraison devient impossible, et c'est le
    // service qu'on demande juste en dessous (`timeSlotBookingDelivery`).
    const telephone = (d.telephone ?? '').trim();
    const email = (d.email ?? '').trim();
    if (!telephone)
        avertissements.push('Pas de téléphone : DHL ne pourra pas convenir d’un créneau.');
    if (!email)
        avertissements.push('Pas de courriel destinataire.');
    const enlevement = prochainJourOuvre(d.aujourdhui);
    const livraison = prochainJourOuvre(enlevement);
    const international = pays !== EXPEDITEUR_MOSAIK.address.countryCode;
    const reference = d.ref;
    const pieces = palettes.map((p) => ({
        goodsType: DESIGNATION_MARCHANDISE,
        packageType: typePaletteDhl(p),
        marksAndNumbers: reference,
        numberOfPieces: 1,
        weight: Math.round(p.poids),
        volume: volumePalette(p),
        loadingMeters: metresPlancher(p),
        width: p.largeur,
        height: p.hauteur,
        length: p.longueur,
        stackable: true,
    }));
    const poidsTotal = pieces.reduce((t, p) => t + p.weight, 0);
    const volumeTotal = Number(pieces.reduce((t, p) => t + p.volume, 0).toFixed(3));
    const plancherTotal = Number(pieces.reduce((t, p) => t + p.loadingMeters, 0).toFixed(1));
    /*
     * **Standard par défaut, Direct seulement au-delà de ses limites.**
     *
     * Tout partait en Direct (ELD/ELI) : un transport de lot partiel ou complet,
     * jusqu'à 24,5 t, sans passage en terminal. Pour une ou deux palettes, c'est
     * le mauvais produit, et le plus cher. Les envois faits à la main sur le
     * portail DHL prenaient le Standard (`#MF3405`, `FRT-002374586`) : le
     * groupage, conçu pour ces volumes.
     */
    const L = LIMITES_STANDARD;
    const depassements = [
        poidsTotal > L.poidsTotal ? `${poidsTotal} kg (max ${L.poidsTotal})` : null,
        volumeTotal > L.volumeTotal ? `${volumeTotal} m³ (max ${L.volumeTotal})` : null,
        plancherTotal > L.metresPlancher ? `${plancherTotal} m de plancher (max ${L.metresPlancher})` : null,
        palettes.some((p) => p.poids > L.poidsPalette) ? `une palette de plus de ${L.poidsPalette} kg` : null,
        palettes.some((p) => p.longueur > L.longueur || p.largeur > L.largeur || p.hauteur > L.hauteur)
            ? `une palette de plus de ${L.longueur} × ${L.largeur} × ${L.hauteur} cm` : null,
    ].filter((x) => x !== null);
    const gamme = depassements.length ? 'direct' : 'standard';
    const codeProduit = PRODUITS_DHL[gamme][international ? 'international' : 'domestique'];
    if (gamme === 'direct') {
        avertissements.push(`Hors des limites du Standard — ${depassements.join(', ')} : envoi en Direct (${codeProduit}), `
            + 'un lot partiel, nettement plus cher.');
    }
    if (international) {
        avertissements.push(`Destination ${pays} : envoi international (${codeProduit}), délai et tarif différents.`);
    }
    /*
     * Hors de l'union douanière, DHL réserve sans broncher, mais la marchandise
     * reste bloquée en douane sans ses documents : le manuel DHL demande de les
     * envoyer par mail au service client du pays (`FAC-20260921-39501`, Suisse).
     */
    if (HORS_UNION_DOUANIERE.has(pays)) {
        avertissements.push(`Destination ${pays} hors de l'Union européenne : documents de douane (facture, déclaration `
            + "d'export) à envoyer au service client DHL Freight, sans quoi l'envoi reste bloqué en douane.");
    }
    const body = {
        id: '',
        productCode: codeProduit,
        pickupDate: enlevement,
        requestedDeliveryDate: livraison,
        pickupInstruction: (d.instructionEnlevement ?? '').trim(),
        /*
         * La saisie de l'écran, et elle seule. Les « détails de livraison » de la
         * commande servaient de repli : c'est le bloc Sellsy « Adresse de
         * livraison : … », et l'adresse s'imprimait ainsi dans « Delivery instr. »
         * de l'étiquette (`FAC-20261002-39529`), en plus de l'encart destinataire.
         */
        deliveryInstruction: (d.instructionLivraison ?? '').trim(),
        totalNumberOfPieces: pieces.length,
        totalWeight: poidsTotal,
        totalVolume: volumeTotal,
        totalLoadingMeters: plancherTotal,
        goodsDescription: DESIGNATION_MARCHANDISE,
        goodsValue: Number.isFinite(Number(d.valeur)) ? Number(d.valeur) : 0,
        goodsValueCurrency: 'EUR',
        /*
         * « Senders ref » de l'étiquette : le numéro de commande. `CNR` est la
         * référence expéditeur du contrat DHL (CNR, CNZ, INV). Aucune référence
         * destinataire (`CNZ`) : « Receivers ref » doit rester vide.
         */
        references: [{ qualifier: 'CNR', value: reference.slice(0, 35) }],
        payerCode: { code: 'DAP', location: '' },
        parties: [
            EXPEDITEUR_MOSAIK,
            {
                type: 'Consignee',
                name: nom,
                // Le nom de la personne quand l'entreprise porte déjà la raison
                // sociale : le livreur demande quelqu'un à l'arrivée.
                contactName: (d.entreprise ?? '').trim() ? (d.client ?? '').trim() : '',
                address: {
                    street: rue,
                    // La « 2nd line of address info » du contrat DHL : le complément
                    // saisi, et lui seul. Les détails de livraison n'y vont pas — ils
                    // répétaient l'adresse dans l'encart destinataire.
                    additionalAddressInfo: (d.adresse?.complement ?? '').trim(),
                    cityName: ville,
                    postalCode: normaliserCodePostal(cp, pays),
                    countryCode: pays,
                },
                phone: telephone,
                email,
            },
            ENLEVEMENT_HIOLLE,
        ],
        pieces,
        additionalServices: {
            dangerousGoods: false,
            dropOffByConsignor: false,
            preAdvice: false,
            tailLiftLoading: false,
            // Le client n'a pas de quai : DHL livre au hayon, sur rendez-vous.
            tailLiftUnloading: true,
            timeSlotBookingDelivery: true,
        },
    };
    return {
        body,
        avertissements,
        resume: {
            palettes: pieces.length,
            poidsTotal,
            volumeTotal,
            metresPlancher: plancherTotal,
            enlevement,
            livraison,
            codeProduit,
            gamme,
            international,
        },
    };
}
/**
 * Ce que chaque champ de l'instruction DHL représente, pour dire à l'écran ce
 * qui a changé depuis l'essai. Un champ absent d'ici se nomme par sa clé.
 */
const LIBELLES_INSTRUCTION_DHL = {
    productCode: 'produit DHL',
    pickupDate: 'date d’enlèvement',
    requestedDeliveryDate: 'date de livraison',
    pickupInstruction: 'instruction d’enlèvement',
    deliveryInstruction: 'instruction de livraison',
    totalNumberOfPieces: 'colisage',
    totalWeight: 'colisage',
    totalVolume: 'colisage',
    totalLoadingMeters: 'colisage',
    pieces: 'colisage',
    goodsDescription: 'désignation',
    goodsValue: 'valeur déclarée',
    goodsValueCurrency: 'valeur déclarée',
    references: 'référence',
    payerCode: 'incoterm',
    parties: 'adresses',
    additionalServices: 'services',
};
/** JSON à clés triées : deux objets égaux s'écrivent pareil, quel que soit leur ordre. */
function jsonStable(v) {
    if (Array.isArray(v))
        return `[${v.map(jsonStable).join(',')}]`;
    if (v && typeof v === 'object') {
        return `{${Object.keys(v).sort()
            .map((k) => `${JSON.stringify(k)}:${jsonStable(v[k])}`).join(',')}}`;
    }
    return JSON.stringify(v ?? null);
}
/**
 * Ce qui distingue la demande ESSAYÉE en bac à sable de celle qu'on s'apprête
 * à envoyer en production, en clair et sans doublon. Vide : ce sont les mêmes.
 *
 * C'est le verrou de la production : on n'y envoie que ce qu'on a vu passer en
 * bac à sable — étiquettes comprises. La date d'enlèvement en fait partie : un
 * essai de la veille ne vaut pas pour une demande qui partirait un autre jour.
 */
export function ecartsInstructionDhl(essayee, actuelle) {
    const cles = new Set([...Object.keys(essayee), ...Object.keys(actuelle)]);
    const ecarts = new Set();
    for (const cle of cles) {
        if (jsonStable(essayee[cle]) !== jsonStable(actuelle[cle])) {
            ecarts.add(LIBELLES_INSTRUCTION_DHL[cle] ?? cle);
        }
    }
    return [...ecarts];
}
/**
 * Code postal au format du pays.
 *
 * Le complément à cinq chiffres du scénario n8n n'est juste qu'en France : il
 * transformerait le `1000` belge en `01000`, qui n'existe pas, et tronquerait
 * un code britannique alphanumérique. On ne complète donc qu'en France.
 */
export function normaliserCodePostal(cp, pays) {
    const brut = String(cp ?? '').trim().replace(/\s+/g, ' ');
    if (pays !== 'FR')
        return brut;
    return /^\d{1,5}$/.test(brut) ? brut.padStart(5, '0') : brut;
}
//# sourceMappingURL=enlevement.js.map